import React, { useEffect, useState } from "react";
import { Stack, Icon, Typography, IconButton } from "@mui/material";
import ReactMarkdown from "react-markdown";
import TieredList from "common/components/TieredList";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import "react18-json-view/src/dark.css";
import remarkGfm from "remark-gfm";
import SidePanel from "common/components/SidePanel";
import ActionButton from "common/components/ActionButton";
import Switch from "common/components/Switch";
import Checkbox from "common/components/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import Input from "common/components/Input";
import E1Input from "common/components/E1Input";

export const JsonVerticalTable = ({ data }) => {
  const [current, setCurrent] = useState(data[0]);
  const [nav, setNav] = useState([]);
  const headers = Object.keys(current);
  useEffect(() => {
    setCurrent(data[0]);
    setNav([]);
  }, [data]);
  const NavLink = ({ item, index }) => {
    return (
      <span
        style={{ cursor: "pointer" }}
        onClick={() => {
          if (index === "root") {
            setNav([]);
            setCurrent(data[0]);
          }
          let place = nav.slice(0, index + 1);
          setNav(place);
          setCurrent(place.reduce((a, c) => a[c], data[0]));
        }}
        index={index}
      >{`${index === "root" ? "" : " / "}${item}`}</span>
    );
  };
  const renderCell = (item) => {
    const urlRegex = /^(https?:\/\/[^\s$.?#].[^\s]*)$/;
    if (typeof item === "boolean") {
      return <Checkbox text="" disabled value={item} />;
    } else if (typeof item === "string" && urlRegex.test(item)) {
      return (
        <a href={item} target="_blank" rel="noopener noreferrer">
          {item}
        </a>
      );
    }
    return <span style={{ wordWrap: "break-word" }}>{item}</span>;
  };
  return (
    <div className="floating">
      <Typography variant="h6">
        <NavLink item="Resource" index="root" />
        {nav.map((n, i) => (
          <NavLink item={n} index={i} />
        ))}
      </Typography>
      <table
        className="resourceTable"
        style={{
          width: "100%",
          minWidth: "600px",
          tableLayout: "fixed",
          borderRadius: "7px",
          borderCollapse: "separate",
          borderSpacing: "10px 10px",
        }}
      >
        <tbody>
          {headers.map((header) => (
            <tr key={header}>
              <td
                style={{
                  width: "250px",
                  minWidth: "250px",
                  wordWrap: "break-word",
                  padding: "10px",
                  background: "#1e1e1e",
                  fontWeight: "bold",
                  borderRadius: "10px",
                  position: "sticky",
                  left: 0,
                }}
              >
                {header}
              </td>
              <td
                style={{
                  wordWrap: "break-word",
                  padding: "10px",
                  borderRadius: "10px",
                  minWidth: "350px",
                }}
              >
                {current[header] !== Object(current[header]) ? (
                  renderCell(current[header])
                ) : (
                  <Icon
                    onClick={() => {
                      setNav([...nav, header]);
                      setCurrent(current[header]);
                    }}
                  >
                    arrow_forward
                  </Icon>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export function replaceVariables(template, values) {
  return template.replace(/\$(\w+)/g, (match, key) => {
    return values.hasOwnProperty(key) ? values[key] : match;
  });
}

export default function ResourceModal({
  refresh,
  system,
  open,
  setOpen,
  colors,
  params,
  selectAndOpenCommand,
}) {
  return (
    <SidePanel
      open={open}
      width={900}
      closeDrawer={() => {
        setOpen(null);
      }}
    >
      {({ onClose }) => {
        const [json, setJson] = React.useState({});
        const [search, setSearch] = React.useState("");
        React.useEffect(() => {
          if (!open) return;
          setJson(open);
        }, [open]);
        const detectorTriggerTier = (open?.detections ?? []).map((d) => {
          let detector = system.detectors.find(({ id }) => id === d);
          let triggers =
            detector?.triggers?.filter((t) => {
              return open?.triggers?.includes(t.id);
            }) ?? [];
          let subItems = triggers.map((t) => ({
            id: t.id,
            component: (
              <div
                style={{
                  marginLeft: "10px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon color="success">chevron_right</Icon>
                <span>{t.name}</span>
              </div>
            ),
          }));
          return {
            id: d,
            component: (
              <div
                style={{
                  marginLeft: "10px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon>{detector.icon}</Icon>
                <span style={{ marginLeft: "10px" }}>{detector.name}</span>
              </div>
            ),
            items: subItems,
          };
        });
        const filtered = Object.fromEntries(
          Object.entries(json).filter(([key, value]) => {
            if (typeof value === "object" && value !== null) {
              return (
                Object.values(value).some((v) =>
                  String(v)
                    .toLowerCase()
                    .includes(search.toLowerCase())
                ) || key.toLowerCase().includes(search.toLowerCase())
              );
            }
            return (
              String(value)
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              key.toLowerCase().includes(search.toLowerCase())
            );
          })
        );
        if (!open) return null;
        return (
          <Stack spacing={2} padding={3}>
            <Stack alignItems={"center"} direction={"row"} gap={1}>
              <Icon>document_scanner</Icon>
              <Typography variant="body1" sx={{ width: "calc(100% - 250px)" }}>
                {open[system.uid]}
              </Typography>
              {system?.actions?.filter(({ selection }) => selection)?.length ? (
                <span style={{ width: "200px", marginLeft: "auto" }}>
                  <ActionButton
                    icon="bolt"
                    dark
                    noload
                    handler={() => {
                      selectAndOpenCommand(open[system.uid]);
                      onClose();
                      // await updateLogoPicture();
                    }}
                  >
                    Run Command
                  </ActionButton>
                </span>
              ) : null}
            </Stack>
            {system?.resourceTemplate ? (
              <Stack className="floating">
                <div>
                  <ReactMarkdown
                    linkTarget="_blank"
                    className="react-markdown-custom"
                    remarkPlugins={[remarkGfm]}
                  >
                    {replaceVariables(system?.resourceTemplate, open || {})}
                  </ReactMarkdown>
                </div>
              </Stack>
            ) : null}
            {Object.keys(filtered).length ? (
              <>
                {/* <JsonVerticalTable data={[filtered]} /> */}
                <Stack
                  sx={{ wordBreak: "break-all", overflowWrap: "break-word" }}
                  spacing={1}
                  className="floating"
                >
                  <Stack gap={1} direction={"row"}>
                    <Typography sx={{ fontWeight: "bold" }} variant="h6">
                      Resource JSON
                    </Typography>
                    <Input
                      dark
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon>search</Icon>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setSearch("")}
                              edge="end"
                            >
                              {search ? (
                                <Icon
                                  sx={{ fontSize: "18px !important" }}
                                  color="error"
                                >
                                  close
                                </Icon>
                              ) : null}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="search..."
                      style={{ marginLeft: "auto", width: "70%" }}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Stack>
                  <JsonView displaySize src={filtered} />
                </Stack>
              </>
            ) : (
              <Typography variant="body1">
                No data matches the search string
              </Typography>
            )}
            {system?.detectors?.length ? (
              <Stack className="floating" gap={2}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Icon>query_stats</Icon>
                  <Typography sx={{ marginLeft: "10px" }} variant="h6">
                    Detectors
                  </Typography>
                </div>
                {!detectorTriggerTier?.length ? (
                  <Typography variant="body2">No detections found</Typography>
                ) : (
                  <TieredList width="450px" items={detectorTriggerTier} />
                )}
              </Stack>
            ) : null}
          </Stack>
        );
      }}
    </SidePanel>
  );
}
